import React, { useContext, useState, useEffect, useMemo } from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import { fetchReportFilterMetadata } from "../api/app";
import { useAuth } from "./AuthProvider";
// import { fetchPermittedCustomProjects } from "../api/custom_projects";
import { useMatch, useNavigate } from "react-router-dom";
import { useNotification } from "./NotificationProvider";

const PersistedStateContext = React.createContext();

export function usePersistedState() {
  return useContext(PersistedStateContext);
}

export function PersistedStateProvider({ children }) {
  const navigate = useNavigate();
  const { setNotification } = useNotification();

  const match = useMatch("/custom-projects/:projectId/:rest/*");
  const projectId = useMemo(() => {
    return match ? match.params.projectId : null;
  }, [match]);

  const { featurePermissions } = useAuth();
  const [reports, setReports] = useState(null);
  const [organizations, setOrganizations] = useState(null);
  const [variables, setVariables] = useState(null);
  // JP - Aug 7, 2023 | Created an audience variables state for the build audience page. To allow for wave to show up on the report builder but not audience builder.
  const [audienceVariables, setAudienceVariables] = useState(null);
  const [audiences, setAudiences] = useState(null);
  // const [appMetadata, setAppMetadata] = useState(null);
  const [reportFilterMetadata, setReportFilterMetadata] = useState(null);
  const [recentReports, setRecentReports] = useLocalStorage(`recentReports`, [
    undefined,
  ]);

  // Custom Projects variables
  const [defaultCustomProject, setDefaultCustomProject] = useLocalStorage(
    "default_custom_project",
    undefined
  );
  const [isRecontact, setIsRecontact] = useState(null);
  const [permittedCustomProjects, setPermittedCustomProjects] = useState([]);
  const [customVariables, setCustomVariables] = useState(null);
  const [customAudienceVariables, setCustomAudienceVariables] = useState(null);
  const [customAudiences, setCustomAudiences] = useState(null);

  const [internationalVariables, setInternationalVariables] = useState(null);
  const [internationalAudienceVariables, setInternationalAudienceVariables] =
    useState(null);
  const [internationalAudiences, setInternationalAudiences] = useState(null);

  const aController = new AbortController();

  const getReportFilterMetadata = async () => {
    try {
      let result = await fetchReportFilterMetadata({ aController });

      if (result?.error) throw new Error(result.error.message);

      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const appendRecent = (reportData) => {
    let { id, report_name } = reportData || {};

    let rReports = Array.isArray(recentReports) ? [...recentReports] : [];

    if (!id || !report_name) return;

    if (rReports.some((report) => report.id === id)) {
      let foundIndex = rReports.indexOf((report) => report.id === id);

      if (foundIndex < 0) return;

      rReports[foundIndex] = { id, report_name };
      return setRecentReports(rReports);
    }

    if (rReports.length < 15) {
      setRecentReports([...rReports, { id, report_name }]);
    } else {
      setRecentReports([...rReports, { id, report_name }].slice(1));
    }
  };

  const updateDefaultCustomProject = (projectId) => {
    if (!projectId) return;
    const projectId_ = Number(projectId);
    const id_ = [];
    id_.push(projectId_);
    setDefaultCustomProject(id_);
  };

  const hasCustomProjectPermissions = (permittedCustomProjects, projectId) => {
    let hasPermissions = false;
    if (
      permittedCustomProjects.some(
        (project) => project.key === Number(projectId)
      )
    ) {
      hasPermissions = true;
    }
    return hasPermissions;
  };

  const permissionsDenied = () => {
    setNotification({
      message: "You do not have access to this project.",
      iconName: "info circle",
      hex: "var(--info-1)",
    });
    setTimeout(() => {
      navigate("/");
    }, 100);
  };

  const value = {
    reports: {
      set: setReports,
      value: reports,
    },
    variables: {
      set: setVariables,
      value: variables,
    },
    // JP - Aug 7, 2023 | Created an audience variables state for the build audience page. To allow for wave to show up on the report builder but not audience builder.
    audienceVariables: {
      set: setAudienceVariables,
      value: audienceVariables,
    },
    audiences: {
      set: setAudiences,
      value: audiences,
    },
    organizations: {
      set: setOrganizations,
      value: organizations,
    },
    recentReports: {
      value: recentReports,
      append: appendRecent,
    },
    metadata: {
      // app: {
      //   value: appMetadata,
      //   refresh: getAppMetadata,
      // },
      reports: {
        value: reportFilterMetadata,
        refresh: getReportFilterMetadata,
      },
    },
    international_fan_insights: {
      variables: {
        set: setInternationalVariables,
        value: internationalVariables,
      },
      audienceVariables: {
        set: setInternationalAudienceVariables,
        value: internationalAudienceVariables,
      },
      audiences: {
        set: setInternationalAudiences,
        value: internationalAudiences,
      },
    },
    custom_projects: {
      defaultCustomProject: {
        value: defaultCustomProject,
        set: setDefaultCustomProject,
      },
      defaultIsRecontact: isRecontact,
      permittedCustomProjects: {
        value: permittedCustomProjects,
        set: setPermittedCustomProjects,
      },
      variables: {
        set: setCustomVariables,
        value: customVariables,
      },
      audienceVariables: {
        set: setCustomAudienceVariables,
        value: customAudienceVariables,
      },
      audiences: {
        set: setCustomAudiences,
        value: customAudiences,
      },
    },
  };

  useEffect(() => {
    if (featurePermissions?.is_faninsights_enabled === true) {
      (async () => {
        setReportFilterMetadata(await getReportFilterMetadata());
      })();
    }
  }, [featurePermissions?.is_faninsights_enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (featurePermissions?.is_custom_projects_enabled === true) {
      (async () => {
        // const permittedCustomProjects = await getPermittedCustomProjects();
        const permittedCustomProjects = featurePermissions?.custom_projects.map(
          (project) => ({
            key: project.id,
            label: project.name,
            is_recontact: project.is_recontact,
          })
        );
        setPermittedCustomProjects(permittedCustomProjects);

        // If there are permitted custom projects, set the default custom project to the first in the array, the projectId in in the URL, or what is held in local storage
        if (permittedCustomProjects.length > 0) {
          // There is no Local Storage
          if (
            !Array.isArray(defaultCustomProject) ||
            defaultCustomProject.length < 1
          ) {
            if (projectId) {
              // Use the projectId in the URL

              updateDefaultCustomProject(projectId);
            } else {
              updateDefaultCustomProject(permittedCustomProjects[0].key);
            }
          }
          // There is Local Storage
          else {
            // There is a projectId
            if (projectId) {
              if (
                hasCustomProjectPermissions(permittedCustomProjects, projectId)
              ) {
                updateDefaultCustomProject(projectId);
              } else {
                permissionsDenied();
              }
            } else {
              let storedCustomProjectKey = "";

              if (Array.isArray(defaultCustomProject)) {
                storedCustomProjectKey = defaultCustomProject[0];
              }

              if (
                !permittedCustomProjects
                  .map((i) => i.key)
                  .includes(storedCustomProjectKey) ||
                !storedCustomProjectKey
              ) {
                updateDefaultCustomProject(permittedCustomProjects[0].key);
              }
            }
          }
        }
      })();
    }
  }, [featurePermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultCustomProject && permittedCustomProjects) {
      const foundProject = permittedCustomProjects.find(
        (project) => project.key === defaultCustomProject[0]
      );

      if (foundProject) setIsRecontact(foundProject.is_recontact);
    }
  }, [defaultCustomProject, permittedCustomProjects]);

  return (
    <PersistedStateContext.Provider value={value}>
      {children}
    </PersistedStateContext.Provider>
  );
}
